import { useQuery } from '@tanstack/react-query';
import { api } from '../client';

export interface ReportRange {
  since: number;
  until: number;
}

export interface BillableHoursRow {
  user_id: number;
  user_display_name: string;
  project_id: number;
  project_case_number: string;
  total_hours: string;
  billable_value: string;
  unbilled_entries: number;
}

export interface ExpenseByTaxRow {
  tax_category: string;
  expense_count: number;
  total_amount: string;
  reimbursable_amount: string;
}

export interface RevenueRow {
  month: string;
  invoice_count: number;
  billed_total: string;
  paid_total: string;
  outstanding_total: string;
  overdue_total: string;
}

export interface UtilizationRow {
  user_id: number;
  user_display_name: string;
  hours_logged: string;
  target_hours: number;
  utilization_pct: string;
}

// projectId is threaded into the query string AND the query key, so switching
// case scopes refetches instead of serving the previous project's rows.
// Revenue and the QuickBooks export never pass one — invoices.project_id is
// nullable, so a filtered money total would silently omit client-level and
// multi-project invoices.
function rangeQs({ since, until }: ReportRange, projectId?: number | null): string {
  const p = new URLSearchParams();
  p.set('since', String(since));
  p.set('until', String(until));
  if (projectId) p.set('project_id', String(projectId));
  return p.toString();
}

export function useBillableHoursReport(range: ReportRange | null, projectId?: number | null) {
  return useQuery({
    queryKey: ['reports', 'billable-hours', range, projectId ?? null],
    queryFn: ({ signal }) =>
      api.get<{ since: number; until: number; rows: BillableHoursRow[] }>(
        `/api/reports/billable-hours?${rangeQs(range!, projectId)}`, signal),
    enabled: range !== null,
  });
}

export function useExpensesByTaxReport(range: ReportRange | null, projectId?: number | null) {
  return useQuery({
    queryKey: ['reports', 'expenses-by-tax', range, projectId ?? null],
    queryFn: ({ signal }) =>
      api.get<{ since: number; until: number; rows: ExpenseByTaxRow[] }>(
        `/api/reports/expenses-by-tax-category?${rangeQs(range!, projectId)}`, signal),
    enabled: range !== null,
  });
}

export function useRevenueReport(range: ReportRange | null) {
  return useQuery({
    queryKey: ['reports', 'revenue', range],
    queryFn: ({ signal }) =>
      api.get<{ since: number; until: number; rows: RevenueRow[] }>(
        `/api/reports/revenue-summary?${rangeQs(range!)}`, signal),
    enabled: range !== null,
  });
}

export function useUtilizationReport(range: ReportRange | null, projectId?: number | null) {
  return useQuery({
    queryKey: ['reports', 'utilization', range, projectId ?? null],
    queryFn: ({ signal }) =>
      api.get<{ since: number; until: number; target_hours: number; rows: UtilizationRow[] }>(
        `/api/reports/utilization?${rangeQs(range!, projectId)}`, signal),
    enabled: range !== null,
  });
}

// Work in progress: logged time + reimbursable expenses not yet sent to the
// client. `unbilled_expense_total` is absent when the caller lacks
// `expense.read` — omitted rather than zeroed, so the UI can tell "no
// permission" apart from "nothing owed".
export interface UnbilledWipRow {
  project_id: number;
  project_case_number: string;
  project_title: string;
  client_name: string | null;
  unbilled_hours: string;
  unbilled_time_value: string;
  unbilled_expense_total?: string;
  wip_total: string;
  oldest_unbilled_at: string | null;
}

export interface UnbilledWipTotals {
  project_count: number;
  unbilled_hours: string;
  unbilled_time_value: string;
  unbilled_expense_total?: string;
  wip_total: string;
}

/**
 * WIP is a BALANCE as of now, not a flow over a window, so this hook takes no
 * date range — the Reports toolbar deliberately does not apply to it.
 * Pass a projectId to scope it to one case.
 */
export function useUnbilledWipReport(projectId?: number | null) {
  return useQuery({
    queryKey: ['reports', 'unbilled-wip', projectId ?? null],
    queryFn: ({ signal }) =>
      api.get<{ rows: UnbilledWipRow[]; totals: UnbilledWipTotals }>(
        `/api/reports/unbilled-wip${projectId ? `?project_id=${projectId}` : ''}`, signal),
  });
}

// Profit & loss — CASH basis (GET /api/reports/profit-and-loss). Income is
// money received in the range, split across each paid invoice's lines; the
// arithmetic lives in api billing/profit-and-loss.ts. Money fields are 2-dp
// strings.
export interface ProfitAndLossReport {
  basis: 'cash';
  since: number;
  until: number;
  payment_count: number;
  income: {
    services: string;
    product_sales: string;
    reimbursed_expenses: string;
    other: string;
    /** Equipment sold outside an invoice, on the date it left (0060). */
    equipment_sales: string;
    total: string;
  };
  /** Collected on invoices, owed to the state — not income. */
  sales_tax_collected: string;
  expenses: {
    by_category: { category: string; amount: string }[];
    contractor_payments: string;
    total: string;
  };
  net_income: string;
  /** Informational: each device invoiced in the range. Not in the totals. */
  device_sales: {
    asset_id: number;
    asset_name: string;
    /** NULL for equipment sold outside an invoice (0060). */
    invoice_id: number | null;
    invoice_number: string | null;
    /** Invoice issue date — or, sold outside an invoice, the date it left. */
    issue_date: number;
    invoice_paid: boolean;
    sale_amount: string;
    cost: string;
    profit: string;
  }[];
}

export function useProfitAndLossReport(range: ReportRange | null) {
  return useQuery({
    queryKey: ['reports', 'profit-and-loss', range],
    queryFn: ({ signal }) =>
      api.get<ProfitAndLossReport>(`/api/reports/profit-and-loss?${rangeQs(range!)}`, signal),
    enabled: range !== null,
  });
}
